import React, { useState } from "react";
import { useAccount, useConfig, useDeployContract, useReadContract } from "wagmi";
import { waitForTransactionReceipt, writeContract } from "@wagmi/core";
import { LYRE_CONSTRUCTOR_ABI, LYRE_BYTECODE } from "../lyreBytecode";

const ENS_REGISTRY = "0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e";
const ENS_REGISTRY_ABI = [
  {
    name: "setResolver",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "node", type: "bytes32" },
      { name: "resolver", type: "address" },
    ],
    outputs: [],
  },
  {
    name: "resolver",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "node", type: "bytes32" }],
    outputs: [{ name: "", type: "address" }],
  },
];

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : "";
}

export default function DeployLyre({ collectionId, nftContractAddress, parentNode, ensName, lyreContractAddress }) {
  const { isConnected } = useAccount();
  const config = useConfig();
  const [deployStatus, setDeployStatus] = useState("idle");
  const [resolverStatus, setResolverStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [contractAddress, setContractAddress] = useState(lyreContractAddress || null);

  const { deployContractAsync } = useDeployContract();

  const { data: currentResolver, refetch: refetchResolver } = useReadContract({
    address: ENS_REGISTRY,
    abi: ENS_REGISTRY_ABI,
    functionName: "resolver",
    args: [parentNode],
    query: { enabled: !!parentNode && !!contractAddress },
  });

  const resolverIsSet = currentResolver && contractAddress &&
    currentResolver.toLowerCase() === contractAddress.toLowerCase();

  const handleDeploy = async () => {
    setError(null);
    setDeployStatus("deploying");

    try {
      const hash = await deployContractAsync({
        abi: LYRE_CONSTRUCTOR_ABI,
        bytecode: LYRE_BYTECODE,
        args: [nftContractAddress, parentNode],
      });

      setDeployStatus("confirming");

      const receipt = await waitForTransactionReceipt(config, { hash });

      if (!receipt?.contractAddress) {
        throw new Error("No contract address in receipt.");
      }

      setDeployStatus("saving");

      const res = await fetch(`/collections/${collectionId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          collection: { lyre_contract_address: receipt.contractAddress },
        }),
      });

      if (!res.ok) {
        let message = `Save failed (${res.status})`;
        try {
          const data = await res.json();
          if (data.errors) message = data.errors.join(", ");
        } catch {}
        throw new Error(message);
      }

      setContractAddress(receipt.contractAddress);
      setDeployStatus("done");
    } catch (err) {
      setError(err.shortMessage || err.message);
      setDeployStatus("error");
    }
  };

  const handleSetResolver = async () => {
    setError(null);
    setResolverStatus("setting");

    try {
      const hash = await writeContract(config, {
        address: ENS_REGISTRY,
        abi: ENS_REGISTRY_ABI,
        functionName: "setResolver",
        args: [parentNode, contractAddress],
      });

      setResolverStatus("confirming");

      await waitForTransactionReceipt(config, { hash, confirmations: 1 });

      refetchResolver();
      setResolverStatus("done");
    } catch (err) {
      setError(err.shortMessage || err.message);
      setResolverStatus("error");
    }
  };

  if (!isConnected) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Connect your wallet to deploy the Lyre contract.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        {!contractAddress && (deployStatus === "idle" || deployStatus === "error") && (
          <button
            onClick={handleDeploy}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors cursor-pointer"
          >
            Deploy Contract
          </button>
        )}

        {deployStatus === "deploying" && (
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            Waiting for wallet approval...
          </p>
        )}

        {deployStatus === "confirming" && (
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            Deploy transaction submitted. Waiting for confirmation...
          </p>
        )}

        {deployStatus === "saving" && (
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Contract deployed! Saving...
          </p>
        )}

        {contractAddress && (
          <p className="text-sm text-green-600 dark:text-green-400">
            Contract deployed at <span className="font-mono">{contractAddress}</span>
          </p>
        )}
      </div>

      {contractAddress && (
        <div className="flex items-center gap-3">
          {resolverIsSet ? (
            <p className="text-sm text-green-600 dark:text-green-400">
              Resolver set for {ensName || "your ENS name"}
            </p>
          ) : (resolverStatus === "idle" || resolverStatus === "error") ? (
            <button
              onClick={handleSetResolver}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer"
            >
              Set Resolver for {ensName || "ENS name"}
            </button>
          ) : resolverStatus === "setting" ? (
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              Waiting for wallet approval...
            </p>
          ) : resolverStatus === "confirming" ? (
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              Resolver transaction submitted. Waiting for confirmation...
            </p>
          ) : resolverStatus === "done" ? (
            <p className="text-sm text-green-600 dark:text-green-400">
              Resolver set for {ensName || "your ENS name"}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
